import * as React from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import useAutoTranslate from '../hooks/useAutoTranslate'

const BASE_COMMANDER_COUNT = 50
const MAX_COMMANDERS = BASE_COMMANDER_COUNT
const META_KEY = '__commanderAllocation'
const KHAN_CONFIG_KEY = 'khanAttackConfig'
const MODULE_GROUP_ORDER = ['Tower', 'Fortress', 'Camp', 'Storm', 'Foreign', 'Berri', 'Khan', 'Nomad', 'Challenge Han', 'Samurai', 'Rift', 'Defense']

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

const getNestedValue = (source, path) =>
    (Array.isArray(path) ? path : []).reduce(
        (currentValue, key) => (currentValue && typeof currentValue === 'object' ? currentValue[key] : undefined),
        source
    )

const setNestedValue = (target, path, value) => {
    const normalizedPath = Array.isArray(path) ? path.filter(Boolean) : []
    if (normalizedPath.length <= 0 || !target || typeof target !== 'object')
        return

    let cursor = target
    normalizedPath.slice(0, -1).forEach(key => {
        if (!cursor[key] || typeof cursor[key] !== 'object')
            cursor[key] = {}
        cursor = cursor[key]
    })

    cursor[normalizedPath[normalizedPath.length - 1]] = value
}

const isCommanderWhitelistOption = option => {
    const optionKey = String(option?.key ?? '').trim().toLowerCase()
    const optionLabel = String(option?.label ?? '').trim().toLowerCase()
    const whitelistPattern = /whitelist|white\s*list/

    if (!optionKey)
        return false

    return whitelistPattern.test(optionKey) || whitelistPattern.test(optionLabel)
}

export const getCommanderOptions = plugin => {
    const pluginOptions = Array.isArray(plugin?.pluginOptions) ? plugin.pluginOptions : []
    const directCommanderOptions = pluginOptions
        .filter(isCommanderWhitelistOption)
        .map(option => ({
            ...option,
            storagePath: [String(option.key ?? '')]
        }))

    if (pluginOptions.some(option => option?.key === KHAN_CONFIG_KEY))
        directCommanderOptions.push({
            type: 'Text',
            key: `${KHAN_CONFIG_KEY}.commanderWhiteList`,
            label: 'Commander White List',
            storagePath: [KHAN_CONFIG_KEY, 'commanderWhiteList']
        })

    return directCommanderOptions
}

const cleanOptionLabel = label =>
    String(label ?? '')
        .replace(/commander white list/ig, '')
        .replace(/commander whitelist/ig, '')
        .replace(/white list/ig, '')
        .replace(/whitelist/ig, '')
        .replace(/\bcommander\b/ig, '')
        .replace(/\bcom\b/ig, '')
        .replace(/\s+/g, ' ')
        .trim()

const getBaseModuleLabel = pluginName => {
    const normalizedName = String(pluginName ?? '').trim()

    if (/foreign|straini|corbi|alien|bloodcrow|outer realms|outerrealms/i.test(normalizedName))
        return 'Foreign'
    if (/khan/i.test(normalizedName))
        return 'Khan'
    if (/fortress/i.test(normalizedName))
        return 'Fortress'
    if (/storm|aqua forts|aqua fort/i.test(normalizedName))
        return 'Storm'
    if (/tower|barron/i.test(normalizedName))
        return 'Tower'
    if (/beri/i.test(normalizedName))
        return 'Berri'
    if (/nomad/i.test(normalizedName))
        return 'Nomad'
    if (/samurai/i.test(normalizedName))
        return 'Samurai'
    if (/camp/i.test(normalizedName))
        return 'Camp'
    if (/flee|defense/i.test(normalizedName))
        return 'Defense'

    return normalizedName
        .replace(/^Attack\s+/i, '')
        .replace(/\s+\([^)]*\)\s*$/g, '')
        .replace(/\s+Settings$/i, '')
        .trim() || 'Module'
}

const resolveCommanderGroupLabel = (pluginKey, optionKey) => {
    const normalizedPluginKey = String(pluginKey ?? '').trim().toLowerCase()
    const normalizedOptionKey = String(optionKey ?? '').trim().toLowerCase()

    if (
        (normalizedPluginKey === 'attacknomads' || normalizedPluginKey === 'attacksams') &&
        normalizedOptionKey === 'commanderwhitelist'
    )
        return 'Camp'

    if (normalizedPluginKey === 'attacknomads' && normalizedOptionKey === 'challengehancommanderwhitelist')
        return 'Challenge Han'

    return ''
}

const buildShortModuleLabel = (pluginKey, pluginName, optionKey, optionLabel, hasMultipleCommanderOptions) => {
    const forcedGroupLabel = resolveCommanderGroupLabel(pluginKey, optionKey)
    if (forcedGroupLabel)
        return forcedGroupLabel

    const baseLabel = getBaseModuleLabel(pluginName)
    if (hasMultipleCommanderOptions) {
        const cleanedOptionLabel = cleanOptionLabel(optionLabel)
        if (cleanedOptionLabel)
            return cleanedOptionLabel
    }

    return baseLabel
}

export const buildModuleEntries = plugins => {
    const pluginList = Array.isArray(plugins) ? plugins : []

    return pluginList.flatMap(plugin => {
        const commanderOptions = getCommanderOptions(plugin)

        return commanderOptions.map(option => ({
            id: `${plugin.key}::${(option.storagePath ?? [option.key]).join('.')}`,
            pluginKey: plugin.key,
            optionKey: option.key,
            storagePath: Array.isArray(option.storagePath) && option.storagePath.length > 0 ? option.storagePath : [String(option.key ?? '')],
            emptyMeansNone: Boolean(option.emptyMeansNone),
            pluginName: String(plugin.name ?? plugin.key),
            shortLabel: buildShortModuleLabel(plugin.key, plugin.name, option.key, option.label, commanderOptions.length > 1),
            fullLabel:
                commanderOptions.length > 1 && cleanOptionLabel(option.label) ?
                    `${String(plugin.name ?? plugin.key)} - ${cleanOptionLabel(option.label)}` :
                    String(plugin.name ?? plugin.key)
        }))
    })
}

const buildModuleGroups = moduleEntries => {
    const groups = new Map()

    moduleEntries.forEach(moduleEntry => {
        const existingGroup = groups.get(moduleEntry.shortLabel)
        if (existingGroup) {
            existingGroup.moduleIds.push(moduleEntry.id)
            existingGroup.modules.push(moduleEntry)
            return
        }

        groups.set(moduleEntry.shortLabel, {
            key: moduleEntry.shortLabel,
            label: moduleEntry.shortLabel,
            moduleIds: [moduleEntry.id],
            modules: [moduleEntry]
        })
    })

    return Array.from(groups.values()).sort((left, right) => {
        const leftIndex = MODULE_GROUP_ORDER.indexOf(left.label)
        const rightIndex = MODULE_GROUP_ORDER.indexOf(right.label)

        if (leftIndex !== -1 && rightIndex !== -1)
            return leftIndex - rightIndex
        if (leftIndex !== -1)
            return -1
        if (rightIndex !== -1)
            return 1

        return left.label.localeCompare(right.label)
    })
}

const parseCommanderPositions = (value, emptyMeansAll = true) => {
    const text = String(value ?? '').trim()
    if (!text)
        return emptyMeansAll ?
            new Set(Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1)) :
            new Set()

    const positions = new Set()
    text
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
        .forEach(part => {
            if (part.includes('-')) {
                const [start, end] = part.split('-').map(piece => Number(piece.trim()))
                if (!Number.isFinite(start) || !Number.isFinite(end))
                    return

                const min = Math.max(1, Math.min(start, end))
                const max = Math.min(MAX_COMMANDERS, Math.max(start, end))
                for (let current = min; current <= max; current += 1)
                    positions.add(current)
                return
            }

            const numericValue = Number(part)
            if (Number.isFinite(numericValue) && numericValue >= 1 && numericValue <= MAX_COMMANDERS)
                positions.add(numericValue)
        })

    return positions
}

const serializeCommanderPositions = positions => {
    const sorted = Array.from(new Set(positions))
        .map(value => Number(value))
        .filter(value => Number.isFinite(value) && value >= 1 && value <= MAX_COMMANDERS)
        .sort((left, right) => left - right)

    if (sorted.length <= 0)
        return ''

    const segments = []
    let rangeStart = sorted[0]
    let previous = sorted[0]

    for (let index = 1; index < sorted.length; index += 1) {
        const current = sorted[index]
        if (current === previous + 1) {
            previous = current
            continue
        }

        segments.push(rangeStart === previous ? `${rangeStart}` : `${rangeStart}-${previous}`)
        rangeStart = current
        previous = current
    }

    segments.push(rangeStart === previous ? `${rangeStart}` : `${rangeStart}-${previous}`)
    return segments.join(',')
}

const createAssignment = (type = 'all', modules = []) => ({
    type,
    modules: Array.from(new Set(Array.isArray(modules) ? modules : []))
})

const normalizeAssignment = (assignment, allModuleIds) => {
    const filteredModules = Array.from(new Set(
        Array.isArray(assignment?.modules) ?
            assignment.modules.filter(moduleId => allModuleIds.includes(moduleId)) :
            []
    ))

    if (assignment?.type === 'skip')
        return createAssignment('skip')
    if (assignment?.type === 'custom')
        return filteredModules.length > 0 ? createAssignment('custom', filteredModules) : createAssignment('skip')
    if (assignment?.type === 'all')
        return createAssignment('all', allModuleIds)

    return filteredModules.length === allModuleIds.length ? createAssignment('all', allModuleIds) : createAssignment('custom', filteredModules)
}

const deriveAssignmentsFromPlugins = (moduleEntries, userPlugins) => {
    const allModuleIds = moduleEntries.map(entry => entry.id)
    const positionsByModule = new Map(
        moduleEntries.map(entry => [
            entry.id,
            parseCommanderPositions(
                getNestedValue(userPlugins?.[entry.pluginKey], entry.storagePath),
                !entry.emptyMeansNone
            )
        ])
    )

    return Object.fromEntries(
        Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).map(position => {
            const enabledModules = moduleEntries
                .filter(entry => positionsByModule.get(entry.id)?.has(position))
                .map(entry => entry.id)

            if (enabledModules.length <= 0)
                return [position, createAssignment('skip')]
            if (enabledModules.length === allModuleIds.length)
                return [position, createAssignment('all', allModuleIds)]

            return [position, createAssignment('custom', enabledModules)]
        })
    )
}

const buildAssignmentsSnapshot = (moduleEntries, userPlugins) => {
    const allModuleIds = moduleEntries.map(entry => entry.id)
    const meta = userPlugins?.[META_KEY] ?? {}
    const derivedAssignments = deriveAssignmentsFromPlugins(moduleEntries, userPlugins)
    const savedAssignments = meta?.assignments && typeof meta.assignments === 'object' ? meta.assignments : {}

    return {
        defaultPolicy: meta?.defaultPolicy === 'skip' ? 'skip' : 'all',
        assignments: Object.fromEntries(
            Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).map(position => {
                const savedAssignment = savedAssignments[position]
                return [
                    position,
                    savedAssignment ?
                        normalizeAssignment(savedAssignment, allModuleIds) :
                        derivedAssignments[position]
                ]
            })
        )
    }
}

const getAssignmentSummary = (assignment, moduleMap) => {
    if (assignment?.type === 'skip')
        return { label: 'Skip', color: 'rgba(120, 132, 154, 0.22)' }
    if (assignment?.type === 'all')
        return { label: 'All Modules', color: 'rgba(86, 109, 146, 0.34)' }

    const labels = Array.from(new Set(
        (assignment?.modules ?? [])
            .map(moduleId => moduleMap.get(moduleId)?.shortLabel)
            .filter(Boolean)
    ))

    if (labels.length <= 0)
        return { label: 'Skip', color: 'rgba(120, 132, 154, 0.22)' }

    return {
        label: labels.length === 1 ? labels[0] : `${labels[0]} +${labels.length - 1}`,
        color: 'rgba(86, 109, 146, 0.34)'
    }
}

const getAssignmentDetailLabel = (assignment, moduleMap) => {
    if (assignment?.type === 'skip')
        return 'Skip'
    if (assignment?.type === 'all')
        return 'All Modules'

    const labels = Array.from(new Set(
        (assignment?.modules ?? [])
            .map(moduleId => moduleMap.get(moduleId)?.shortLabel)
            .filter(Boolean)
    ))
    return labels.length > 0 ? labels.join(', ') : 'Skip'
}

export const buildCommanderRosterDisplayRows = ({ commanderRoster, assignments, moduleMap }) => {
    const liveCommanders = Array.isArray(commanderRoster?.commanders) ? commanderRoster.commanders : []
    const liveByPosition = new Map(
        liveCommanders
            .map(commander => [Number(commander?.position), commander])
            .filter(([position]) => Number.isInteger(position) && position > 0)
    )
    const standardRows = Array.from({ length: MAX_COMMANDERS }, (_, index) => {
        const position = index + 1
        const liveCommander = liveByPosition.get(position)
        return {
            key: `standard-${position}`,
            position,
            name: String(liveCommander?.name ?? '').trim() || `Commander #${position}`,
            assignment: getAssignmentDetailLabel(assignments?.[position], moduleMap),
            synced: Boolean(liveCommander),
            reserveCommander: false,
            special: false
        }
    })
    const specialRows = liveCommanders
        .filter(commander => commander?.standard === false || Number(commander?.position) > MAX_COMMANDERS)
        .map((commander, index) => ({
            key: `special-${commander?.lordID ?? commander?.position ?? index}`,
            position: Number.isInteger(Number(commander?.position)) ? Number(commander.position) : undefined,
            name: String(commander?.name ?? '').trim() || 'Special commander',
            assignment: commander?.extraCommander ?
                'Castle Defense (when Extra Commander is enabled)' :
                'VIP / daily commander mode',
            synced: true,
            reserveCommander: Boolean(commander?.extraCommander),
            special: true
        }))

    const extraCommanderRow = {
        key: 'extra-commander',
        position: undefined,
        name: 'Extra Commander',
        assignment: 'Castle Defense (when Extra Commander is enabled)',
        synced: true,
        reserveCommander: true,
        special: true
    }

    return [...standardRows, extraCommanderRow, ...specialRows]
}

const toggleAssignmentModules = (assignment, moduleIds, allModuleIds) => {
    const currentModules =
        assignment?.type === 'all' ? allModuleIds :
            assignment?.type === 'custom' ? assignment.modules :
                []
    const hasEveryModuleSelected =
        assignment?.type === 'all' ||
        moduleIds.every(moduleId => currentModules.includes(moduleId))

    const nextModules = hasEveryModuleSelected ?
        currentModules.filter(currentModuleId => !moduleIds.includes(currentModuleId)) :
        [...currentModules, ...moduleIds.filter(moduleId => !currentModules.includes(moduleId))]

    if (nextModules.length <= 0)
        return createAssignment('skip')
    if (nextModules.length === allModuleIds.length)
        return createAssignment('all', allModuleIds)

    return createAssignment('custom', nextModules)
}

export const applyCommanderAssignments = (plugins, userPlugins, defaultPolicy, assignments) => {
    const nextPlugins = cloneValue(userPlugins ?? {})
    const moduleEntries = buildModuleEntries(plugins)

    moduleEntries.forEach(entry => {
        const allowedPositions = Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).filter(position => {
            const assignment = assignments[position] ?? createAssignment(defaultPolicy === 'skip' ? 'skip' : 'all')

            if (assignment.type === 'skip')
                return false
            if (assignment.type === 'all')
                return true

            return assignment.modules.includes(entry.id)
        })

        nextPlugins[entry.pluginKey] ??= {}
        setNestedValue(nextPlugins[entry.pluginKey], entry.storagePath, serializeCommanderPositions(allowedPositions))
    })

    nextPlugins[META_KEY] = {
        defaultPolicy: defaultPolicy === 'skip' ? 'skip' : 'all',
        assignments: Object.fromEntries(
            Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).map(position => [position, assignments[position]])
        )
    }

    return nextPlugins
}

function SummaryChip({ label }) {
    return (
        <Chip
            label={label}
            size="small"
            sx={{
                borderRadius: '999px',
                backgroundColor: 'rgba(86, 109, 146, 0.34)',
                color: '#f5f7ff',
                fontWeight: 700,
                '& .MuiChip-label': { px: 1.2 }
            }}
        />
    )
}

function ActionButton({ active = false, partial = false, children, onClick, minWidth = 0 }) {
    return (
        <Button
            variant="outlined"
            onClick={onClick}
            sx={{
                minWidth,
                minHeight: 40,
                px: 2,
                borderRadius: '11px',
                textTransform: 'none',
                fontSize: '0.96rem',
                fontWeight: 800,
                color: '#f5f7ff',
                borderColor:
                    active ? 'rgba(122, 176, 255, 0.82)' :
                        partial ? 'rgba(122, 176, 255, 0.46)' :
                            'rgba(129, 146, 178, 0.28)',
                backgroundColor:
                    active ? 'rgba(104, 137, 194, 0.28)' :
                        partial ? 'rgba(104, 137, 194, 0.16)' :
                            'rgba(255,255,255,0.02)'
            }}
        >
            {children}
        </Button>
    )
}

export default function CommanderAllocationPanel({
    plugins,
    userPlugins,
    onChange,
    commanderRoster,
    appLanguage = 'en'
}) {
    const t = useAutoTranslate(appLanguage, [
        'Commander Allocation',
        'Assign commanders to modules and define the fallback policy.',
        'Unassigned Commander Policy',
        'Commanders without an explicit assignment follow this policy.',
        'Set Unassigned Commander Policy to Skip if you want commander selections to apply only to the attack types you assign.',
        'Assign to All',
        'Skip',
        'Configure commander assignments. Unlisted commanders default to All Modules and are saved explicitly.',
        'Open Commander Assignment Editor',
        'Clear All Assignments',
        'Bulk Actions',
        'Apply settings to all commander slots at once.',
        'Set All to All Modules',
        'Set All to Skip',
        'Commander Assignments',
        'Each row includes inline module buttons, including All Modules and Skip Commander.',
        `Configure commander slots 1-${MAX_COMMANDERS}. Use the inline module buttons on each row to customize individual commanders.`,
        `Commander Allocation only manages the standard commander slots 1-${BASE_COMMANDER_COUNT}. VIP and daily commanders are excluded here and handled separately by VIP mode.`,
        'Changes apply once you click Save.',
        'Closing without saving discards updates.',
        'Cancel',
        'Save Assignments',
        'Live Commander Roster',
        'Game name and current bot assignment for every commander.',
        'Waiting for live commander name'
    ])

    const moduleEntries = React.useMemo(() => buildModuleEntries(plugins), [plugins])
    const moduleGroups = React.useMemo(() => buildModuleGroups(moduleEntries), [moduleEntries])
    const moduleMap = React.useMemo(() => new Map(moduleEntries.map(entry => [entry.id, entry])), [moduleEntries])
    const allModuleIds = React.useMemo(() => moduleEntries.map(entry => entry.id), [moduleEntries])
    const snapshot = React.useMemo(() => buildAssignmentsSnapshot(moduleEntries, userPlugins), [moduleEntries, userPlugins])

    const [editorOpen, setEditorOpen] = React.useState(false)
    const [draftAssignments, setDraftAssignments] = React.useState(snapshot.assignments)

    React.useEffect(() => {
        if (!editorOpen)
            setDraftAssignments(snapshot.assignments)
    }, [editorOpen, snapshot.assignments])

    const defaultPolicy = snapshot.defaultPolicy
    const assignments = snapshot.assignments
    const rosterRows = React.useMemo(
        () => buildCommanderRosterDisplayRows({ commanderRoster, assignments, moduleMap }),
        [assignments, commanderRoster, moduleMap]
    )
    const commanderNamesByPosition = React.useMemo(
        () => new Map(rosterRows.filter(row => !row.special).map(row => [row.position, row.name])),
        [rosterRows]
    )
    const summaryEntries = Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).map(position => {
        const summary = getAssignmentSummary(assignments[position], moduleMap)
        return {
            position,
            label: `#${position} ${commanderNamesByPosition.get(position)}: ${summary.label}`
        }
    })
    const visibleSummaryEntries = summaryEntries.slice(0, 3)
    const hiddenSummaryCount = Math.max(0, summaryEntries.length - visibleSummaryEntries.length)
    const assignmentCounts = React.useMemo(() => {
        const values = Object.values(draftAssignments ?? {})
        return {
            custom: values.filter(assignment => assignment?.type === 'custom').length,
            skipped: values.filter(assignment => assignment?.type === 'skip').length
        }
    }, [draftAssignments])

    const persistAssignments = React.useCallback((nextAssignments, nextDefaultPolicy = defaultPolicy) => {
        onChange(applyCommanderAssignments(plugins, userPlugins, nextDefaultPolicy, nextAssignments))
    }, [defaultPolicy, onChange, plugins, userPlugins])

    const handleDefaultPolicyChange = nextDefaultPolicy => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[META_KEY] ??= {}
        nextPlugins[META_KEY].defaultPolicy = nextDefaultPolicy
        onChange(nextPlugins)
    }

    const handleClearAssignments = () => {
        const nextAssignments = Object.fromEntries(
            Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).map(position => [
                position,
                createAssignment(defaultPolicy === 'skip' ? 'skip' : 'all', allModuleIds)
            ])
        )
        persistAssignments(nextAssignments, defaultPolicy)
    }

    const setCommanderAssignment = React.useCallback((position, nextAssignment) => {
        setDraftAssignments(currentAssignments => ({
            ...currentAssignments,
            [position]: nextAssignment
        }))
    }, [])

    if (moduleEntries.length <= 0) {
        return (
            <Box
                sx={{
                    p: 2.2,
                    borderRadius: 2.8,
                    border: '1px solid rgba(255,255,255,0.06)',
                    backgroundColor: 'rgba(255,255,255,0.03)'
                }}
            >
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#f5f7ff' }}>
                    No active commander modules found
                </Typography>
                <Typography sx={{ mt: 0.8, color: '#9aa6bf', maxWidth: 620 }}>
                    Enable at least one module that uses commander slots and it will appear here for assignment.
                </Typography>
            </Box>
        )
    }

    return (
        <>
            <Stack spacing={2.4}>
                <Box
                    sx={{
                        p: 2.4,
                        borderRadius: '22px',
                        border: '1px solid rgba(129, 146, 178, 0.18)',
                        background: 'linear-gradient(180deg, rgba(21, 33, 55, 0.62) 0%, rgba(18, 29, 48, 0.5) 100%)'
                    }}
                >
                    <Stack spacing={2.2}>
                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 700, color: '#f5f7ff' }}>
                                {t('Unassigned Commander Policy')}
                            </Typography>
                            <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
                                <ActionButton active={defaultPolicy === 'all'} onClick={() => handleDefaultPolicyChange('all')} minWidth={128}>
                                    {t('Assign to All')}
                                </ActionButton>
                                <ActionButton active={defaultPolicy === 'skip'} onClick={() => handleDefaultPolicyChange('skip')} minWidth={128}>
                                    {t('Skip')}
                                </ActionButton>
                            </Stack>
                            <Typography sx={{ mt: 1, color: '#96a5c0', fontSize: '0.96rem' }}>
                                {t('Commanders without an explicit assignment follow this policy.')}
                            </Typography>
                            <Typography sx={{ mt: 0.65, color: '#96a5c0', fontSize: '0.92rem' }}>
                                {t('Set Unassigned Commander Policy to Skip if you want commander selections to apply only to the attack types you assign.')}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography sx={{ color: '#d8e2f3', fontSize: '0.98rem' }}>
                                {t('Configure commander assignments. Unlisted commanders default to All Modules and are saved explicitly.')}
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: '#96a5c0', fontSize: '0.92rem' }}>
                                {t(`Commander Allocation only manages the standard commander slots 1-${BASE_COMMANDER_COUNT}. VIP and daily commanders are excluded here and handled separately by VIP mode.`)}
                            </Typography>
                            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 1.3 }}>
                                {visibleSummaryEntries.map(entry => <SummaryChip key={entry.position} label={entry.label} />)}
                                {hiddenSummaryCount > 0 ? <SummaryChip label={`+${hiddenSummaryCount} more`} /> : null}
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setDraftAssignments(snapshot.assignments)
                                    setEditorOpen(true)
                                }}
                                sx={{
                                    minHeight: 42,
                                    px: 2.2,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    color: '#f5f7ff',
                                    borderColor: 'rgba(129, 146, 178, 0.32)'
                                }}
                            >
                                {t('Open Commander Assignment Editor')}
                            </Button>
                            <Button
                                variant="text"
                                onClick={handleClearAssignments}
                                sx={{
                                    minHeight: 42,
                                    px: 1,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    color: '#d8e2f3'
                                }}
                            >
                                {t('Clear All Assignments')}
                            </Button>
                        </Stack>

                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                        <Box data-testid="commander-live-roster">
                            <Typography sx={{ fontWeight: 800, color: '#f5f7ff' }}>
                                {t('Live Commander Roster')}
                            </Typography>
                            <Typography sx={{ mt: 0.55, color: '#96a5c0', fontSize: '0.92rem' }}>
                                {t('Game name and current bot assignment for every commander.')}
                            </Typography>
                            <Box
                                sx={{
                                    mt: 1.35,
                                    maxHeight: 520,
                                    overflowY: 'auto',
                                    borderRadius: '14px',
                                    border: '1px solid rgba(129, 146, 178, 0.18)'
                                }}
                            >
                                {rosterRows.map((row, index) => (
                                    <Box
                                        key={row.key}
                                        sx={{
                                            px: 1.45,
                                            py: 1.05,
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 0.8fr) minmax(260px, 1.2fr)' },
                                            gap: 1,
                                            alignItems: 'center',
                                            borderBottom: index === rosterRows.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.07)',
                                            backgroundColor: row.reserveCommander ? 'rgba(245,158,11,0.09)' : 'rgba(19,30,49,0.5)'
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                                            <Typography sx={{ minWidth: 32, color: row.special ? '#fbbf24' : '#7ab0ff', fontWeight: 800 }}>
                                                {row.position ? `#${row.position}` : '—'}
                                            </Typography>
                                            <Typography sx={{ color: row.synced ? '#f5f7ff' : '#8d9ab1', fontWeight: 750, overflowWrap: 'anywhere' }}>
                                                {row.name}
                                            </Typography>
                                        </Stack>
                                        <Typography sx={{ color: row.reserveCommander ? '#fcd34d' : '#b9c7dc', fontSize: '0.92rem', overflowWrap: 'anywhere' }}>
                                            {row.synced ? row.assignment : t('Waiting for live commander name')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Stack>
                </Box>
            </Stack>

            <Dialog
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: 560,
                        minHeight: 760,
                        maxHeight: '88vh',
                        borderRadius: '18px',
                        background: 'linear-gradient(180deg, rgba(20,31,51,0.98) 0%, rgba(17,26,44,0.98) 100%)',
                        border: '1px solid rgba(129, 146, 178, 0.16)',
                        color: '#f5f7ff'
                    }
                }}
            >
                <DialogTitle sx={{ px: 2.4, pt: 2.1, pb: 1.7 }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                        <Box>
                            <Typography sx={{ fontSize: '1.65rem', fontWeight: 800 }}>
                                Commander Assignment Editor
                            </Typography>
                            <Typography sx={{ mt: 0.8, color: '#c2cede', fontSize: '0.98rem', maxWidth: 470 }}>
                                {t(`Configure commander slots 1-${MAX_COMMANDERS}. Use the inline module buttons on each row to customize individual commanders.`)}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setEditorOpen(false)} sx={{ color: '#aebad0', mt: -0.3, mr: -0.6 }}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                <DialogContent sx={{ px: 0, py: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Box sx={{ px: 2.4, py: 2.1 }}>
                        <Typography sx={{ fontSize: '1.02rem', fontWeight: 800, color: '#f5f7ff' }}>
                            {t('Bulk Actions')}
                        </Typography>
                        <Typography sx={{ mt: 0.35, color: '#9aa6bf', fontSize: '0.96rem' }}>
                            {t('Apply settings to all commander slots at once.')}
                        </Typography>

                        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mt: 1.7 }}>
                            <ActionButton
                                onClick={() => setDraftAssignments(Object.fromEntries(
                                    Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).map(position => [
                                        position,
                                        createAssignment('all', allModuleIds)
                                    ])
                                ))}
                                minWidth={168}
                            >
                                {t('Set All to All Modules')}
                            </ActionButton>
                            <ActionButton
                                onClick={() => setDraftAssignments(Object.fromEntries(
                                    Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).map(position => [
                                        position,
                                        createAssignment('skip')
                                    ])
                                ))}
                                minWidth={132}
                            >
                                {t('Set All to Skip')}
                            </ActionButton>
                        </Stack>

                        <Typography sx={{ mt: 2.8, fontSize: '1.02rem', fontWeight: 800, color: '#f5f7ff' }}>
                            {t('Commander Assignments')}
                        </Typography>
                        <Typography sx={{ mt: 0.35, color: '#9aa6bf', fontSize: '0.96rem', maxWidth: 470 }}>
                            {t('Each row includes inline module buttons, including All Modules and Skip Commander.')}
                        </Typography>
                        <Typography sx={{ mt: 1.25, color: '#c1cadb', fontSize: '0.95rem' }}>
                            {`${assignmentCounts.custom} custom assignments, ${assignmentCounts.skipped} skipped`}
                        </Typography>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', px: 2.4, pb: 2.2 }}>
                        <Box
                            sx={{
                                borderRadius: '16px',
                                border: '1px solid rgba(129, 146, 178, 0.18)',
                                overflow: 'hidden'
                            }}
                        >
                            {Array.from({ length: MAX_COMMANDERS }, (_, index) => index + 1).map(position => {
                                const assignment = draftAssignments[position] ?? createAssignment(defaultPolicy === 'skip' ? 'skip' : 'all', allModuleIds)
                                const summary = getAssignmentSummary(assignment, moduleMap)

                                return (
                                    <Box
                                        key={position}
                                        sx={{
                                            px: 1.6,
                                            py: 1.55,
                                            backgroundColor: 'rgba(19, 30, 49, 0.58)',
                                            borderBottom: position === MAX_COMMANDERS ? 'none' : '1px solid rgba(255,255,255,0.08)'
                                        }}
                                    >
                                        <Stack spacing={1.35}>
                                            <Stack direction="row" spacing={1.05} alignItems="center">
                                                <Typography sx={{ minWidth: 32, color: '#7ab0ff', fontWeight: 800 }}>
                                                    {`#${position}`}
                                                </Typography>
                                                <Typography sx={{ color: '#f5f7ff', fontWeight: 750, overflowWrap: 'anywhere' }}>
                                                    {commanderNamesByPosition.get(position)}
                                                </Typography>
                                                <Chip
                                                    label={summary.label}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: '9px',
                                                        backgroundColor: summary.color,
                                                        color: '#f5f7ff',
                                                        fontWeight: 700
                                                    }}
                                                />
                                            </Stack>

                                            <Stack direction="row" spacing={1.05} flexWrap="wrap" useFlexGap>
                                                <ActionButton
                                                    active={assignment?.type === 'all'}
                                                    onClick={() => setCommanderAssignment(position, createAssignment('all', allModuleIds))}
                                                >
                                                    All Modules
                                                </ActionButton>

                                                {moduleGroups.map(group => {
                                                    const selectedCount =
                                                        assignment?.type === 'all' ? group.moduleIds.length :
                                                            assignment?.type === 'custom' ? group.moduleIds.filter(moduleId => assignment.modules.includes(moduleId)).length :
                                                                0
                                                    const selected = assignment?.type === 'all' || selectedCount === group.moduleIds.length
                                                    const partial = !selected && selectedCount > 0

                                                    return (
                                                        <ActionButton
                                                            key={`${position}-${group.key}`}
                                                            active={selected}
                                                            partial={partial}
                                                            onClick={() => setCommanderAssignment(
                                                                position,
                                                                toggleAssignmentModules(assignment, group.moduleIds, allModuleIds)
                                                            )}
                                                        >
                                                            {group.label}
                                                        </ActionButton>
                                                    )
                                                })}

                                                <ActionButton
                                                    active={assignment?.type === 'skip'}
                                                    onClick={() => setCommanderAssignment(position, createAssignment('skip'))}
                                                >
                                                    Skip Commander
                                                </ActionButton>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Box>
                </DialogContent>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                <DialogActions sx={{ px: 2.4, py: 2.1, justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#9aa6bf', fontSize: '0.94rem', maxWidth: 270 }}>
                        {`${t('Changes apply once you click Save.')} ${t('Closing without saving discards updates.')}`}
                    </Typography>

                    <Stack direction="row" spacing={1.1}>
                        <Button
                            variant="outlined"
                            onClick={() => setEditorOpen(false)}
                            sx={{
                                minWidth: 92,
                                minHeight: 42,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                color: '#f5f7ff',
                                borderColor: 'rgba(129, 146, 178, 0.28)'
                            }}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                persistAssignments(draftAssignments, defaultPolicy)
                                setEditorOpen(false)
                            }}
                            sx={{
                                minWidth: 170,
                                minHeight: 42,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 800,
                                backgroundColor: 'rgba(86, 109, 146, 0.92)',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: 'rgba(99, 124, 166, 0.96)',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {t('Save Assignments')}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    )
}
